import { defineApiQuery } from "utils/queries";
import { api } from "./restUtil";
import { StudentStatus, StudentStatusEnum } from "./student/model";

type FavoriteStudentsQuery = {
  student_status?: string;
};

type RawStudentsFavorite = {
  id: string;
  status: string;
  email: string;
  first_name: string;
  last_name: string;
  city: string | null;
  country: string | null;
  timezone: { id: string; utc_offset: string };
  last_seen: string | null;
  number_of_lessons?: number;
  student: { status: StudentStatus };
  primary_instructor: { id: string; first_name: string; last_name: string } | null;
};

type RawStudentsFavoritesResponse = {
  list: RawStudentsFavorite[];
};

export type FavoriteStudent = {
  id: string;
  status: string;
  /** Last activity in the app. The roster uses it to group dormant students. */
  last_seen: string | null;
  profile: {
    email: string;
    first_name: string;
    last_name: string;
    timezone: { id: string; utc_offset: string };
    address: { city: string | null; country: string | null };
  };
  student: {
    number_of_lessons: number | undefined;
    status: StudentStatus;
  };
  primary_instructor: { id: string; first_name: string; last_name: string } | null;
};

type FavoriteStudentListResponse = {
  list: FavoriteStudent[];
};

/**
 * Every student assigned to the signed-in instructor, in every status.
 *
 * The roster asks for all statuses on purpose: finished students are not rendered
 * inline any more, but their count and the disclosure that reveals them need them
 * in the response. Grouping happens on the client in `studentGroups`.
 */
export const favoriteStudentsQuery = defineApiQuery<FavoriteStudentListResponse>({
  key: "/students/favorites",
  queryFn: () =>
    getFavoritesForInstructor({
      student_status: Object.values(StudentStatusEnum).join(","),
    }),
  // A teacher's roster changes on the order of days, and this returns every student
  // they have ever been assigned. The panel invalidates the key itself when it removes
  // someone, so nothing here depends on polling.
  staleTime: 10 * 60 * 1000,
  refetchOnWindowFocus: false,
});

// When called with undefined, applies a default student_status filter (all except graduated).
// When called with null returns all statuses.
export function getFavoritesForInstructor(
  query?: FavoriteStudentsQuery | null
): Promise<FavoriteStudentListResponse> {
  const params: Record<string, string> = { sort: "name" };
  if (query === undefined) {
    params.student_status = Object.values(StudentStatusEnum)
      .filter(status => status !== StudentStatusEnum.GRADUATED)
      .join(",");
  } else if (query !== null) {
    Object.assign(params, query);
  }
  return api
    .get<RawStudentsFavoritesResponse>("/students/favorites", params)
    .then(resp => ({
      list: resp.list.map(s => ({
        id: s.id,
        status: s.status,
        last_seen: s.last_seen,
        profile: {
          email: s.email,
          first_name: s.first_name,
          last_name: s.last_name,
          timezone: { id: s.timezone.id, utc_offset: s.timezone.utc_offset },
          address: { city: s.city, country: s.country },
        },
        student: {
          number_of_lessons: s.number_of_lessons,
          status: s.student.status,
        },
        primary_instructor: s.primary_instructor,
      })),
    }));
}

export function removeStudentFromFavorites(studentId: string): Promise<void> {
  return api.delete<{ id: string }>("/students/favorites", { id: studentId });
}

export function addFavouriteCourse(id: string): Promise<void> {
  return api.post(`/library/${id}/favorite`);
}

export function removeFavouriteCourse(id: string): Promise<void> {
  return api.delete(`/library/${id}/favorite`);
}
